import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import apiConfig from "../../utils/apiConfig";
import { useTheme } from "../../contexts/ThemeContext";
import AuroraBackground from "../../components/AuroraBackground";
import BreathingStars from "../../components/BreathingStars";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      await apiConfig.api.post("/auth/register", {
        email,
        password,
        firstName,
        lastName,
      });
      navigate("/");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to create account");
    }
  };

  return (
    <div className={`relative min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black' : 'bg-white'} overflow-hidden transition-colors duration-200`}>
      {isDarkMode ? <BreathingStars /> : <AuroraBackground />}
      
      <div className="relative flex items-center space-x-16 p-8 z-10">
        <div className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
          <img
            src={isDarkMode ? "/images/logos/q-logo-white.png" : "/images/logos/q-logo-color.png"}
            alt="Retirement Systems"
            className={`w-80 md:w-96 h-auto ${isDarkMode ? 'opacity-90 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]' : 'opacity-100'} transition-all duration-300`}
          />
        </div>

        <div className="w-96">
          <div className={`p-8 ${isDarkMode ? 'backdrop-blur-xl' : ''}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Create an account
              </h2>
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-neutral-800 text-gray-300 hover:bg-neutral-700 hover:shadow-[0_0_15px_rgba(48,255,200,0.2)]' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-[0_0_15px_rgba(40,180,110,0.2)]'
                }`}
              >
                {isDarkMode ? '🌙' : '☀️'}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className={`px-4 py-3 rounded-lg relative ${
                    isDarkMode 
                      ? 'bg-red-900/20 border border-red-800 text-red-200' 
                      : 'bg-red-100 border border-red-400 text-red-700'
                  }`}
                  role="alert"
                >
                  <span className="block sm:inline">{error}</span>
                </div>
              )}

              <div>
                <label
                  htmlFor="firstName"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  First Name:
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Last Name:
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Email:
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Password:
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Confirm Password:
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-[#029831] hover:bg-[#027a28] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831] transition-all duration-300 hover:shadow-[0_0_15px_rgba(2,152,49,0.3)]"
                >
                  Create Account
                </button>
              </div>

              <div className="flex justify-between text-sm">
                <Link
                  to="/"
                  className={`relative group ${
                    isDarkMode 
                      ? 'text-[#03b93b] hover:text-[#029831]' 
                      : 'text-[#029831] hover:text-[#018529]'
                  } transition-colors duration-200`}
                >
                  <span>Back to login</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
